import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { createRoot } from "react-dom/client";
import "./index.css";
import { attemptChunkReload } from "./lib/chunkReload";

// Vite ≥5 emits this when a lazy route's dynamic import fails to preload —
// typically because the tab predates a deploy and the old index.html points
// at chunk hashes that no longer exist. Reload once to pick up the current
// build instead of crashing to the ErrorBoundary.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  attemptChunkReload();
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  environment: window.location.hostname.endsWith("mamacurio.com") ? "production" : "development",
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// PostHog analytics — only initialize in production
if (import.meta.env.VITE_POSTHOG_KEY && window.location.hostname.endsWith("mamacurio.com")) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false, // We capture manually via React Router for SPA accuracy
    capture_pageleave: true,
    autocapture: true,
  });
}

// Safe bootstrap: check env vars BEFORE importing App (which imports Supabase client)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const root = createRoot(document.getElementById("root")!);

if (!supabaseUrl || !supabaseKey) {
  // Render a diagnostic fallback instead of blank screen
  root.render(
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "system-ui, sans-serif",
      backgroundColor: "#1a1a2e",
      color: "#eee",
      padding: "2rem",
    }}>
      <div style={{
        maxWidth: "480px",
        textAlign: "center",
        background: "#16213e",
        padding: "2rem",
        borderRadius: "12px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
      }}>
        <h1 style={{ fontSize: "1.5rem", marginBottom: "1rem", color: "#f97316" }}>
          ⚠️ Startup Configuration Error
        </h1>
        <p style={{ marginBottom: "1rem", lineHeight: 1.6 }}>
          The application could not start because required backend configuration is missing.
        </p>
        <div style={{
          background: "#0f0f23",
          padding: "1rem",
          borderRadius: "8px",
          marginBottom: "1.5rem",
          textAlign: "left",
          fontSize: "0.875rem",
        }}>
          <div style={{ marginBottom: "0.5rem" }}>
            <strong>VITE_SUPABASE_URL:</strong>{" "}
            <span style={{ color: supabaseUrl ? "#22c55e" : "#ef4444" }}>
              {supabaseUrl ? "✓ Present" : "✗ Missing"}
            </span>
          </div>
          <div>
            <strong>VITE_SUPABASE_PUBLISHABLE_KEY:</strong>{" "}
            <span style={{ color: supabaseKey ? "#22c55e" : "#ef4444" }}>
              {supabaseKey ? "✓ Present" : "✗ Missing"}
            </span>
          </div>
        </div>
        <p style={{ fontSize: "0.875rem", color: "#aaa", marginBottom: "1.5rem" }}>
          This is usually an environment injection issue during build.
          Try reloading or check your project's environment configuration.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: "#f97316",
            color: "#fff",
            border: "none",
            padding: "0.75rem 1.5rem",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "1rem",
          }}
        >
          Reload Page
        </button>
      </div>
    </div>
  );
} else {
  // Env vars present — dynamically import App to avoid Supabase client crash
  import("./App").then(({ default: App }) => {
    root.render(<App />);
  }).catch((err) => {
    console.error("Failed to load App:", err);
    Sentry.captureException(err);
    root.render(
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, sans-serif",
        backgroundColor: "#1a1a2e",
        color: "#eee",
        padding: "2rem",
      }}>
        <div style={{ textAlign: "center" }}>
          <h1 style={{ color: "#ef4444" }}>Failed to load application</h1>
          <p style={{ marginTop: "1rem" }}>{String(err)}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1.5rem",
              background: "#f97316",
              color: "#fff",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  });
}
